import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  TimeSeriesScale,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import {
  CandlestickController,
  CandlestickElement,
} from "chartjs-chart-financial";
import "chartjs-adapter-moment";

// Register the chart type and elements
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  TimeSeriesScale,
  Tooltip,
  Legend,
  CandlestickController,
  CandlestickElement
);

const CandlestickChart = ({ completedOrders }) => {
  const [candleData, setCandleData] = React.useState([]);
  const [volumeData, setVolumeData] = React.useState([]);
  const [closePriceData, setClosePriceData] = React.useState([]);

  function formatVolume(value) {
    return `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }

  React.useEffect(() => {
    if (completedOrders.length) {
      processCandleData(completedOrders);
    }
  }, [completedOrders]);

  const processCandleData = (data) => {
    const sortedData = data.sort((a, b) => new Date(a.time) - new Date(b.time));
    let groupedData = {};
    let lastClose = null; // Inicia a variável para manter o último preço de fechamento

    sortedData.forEach((order) => {
      const date = new Date(order.time);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      const hour = String(date.getUTCHours()).padStart(2, "0");
      const timeKey = `${year}-${month}-${day}T${hour}:00:00.000Z`;

      if (!groupedData[timeKey]) {
        groupedData[timeKey] = [];
      }
      groupedData[timeKey].push(order);
    });

    const chartData = [];
    const volumeChartData = [];
    const newClosePriceData = [];
    Object.entries(groupedData).forEach(([timeKey, orders]) => {
      const time = new Date(timeKey).getTime();
      const prices = orders.map((order) => parseFloat(order.price));
      const amounts = orders.map((order) => parseFloat(order.amount));
      const volume = prices.reduce(
        (acc, price, idx) => acc + price * amounts[idx],
        0
      );

      const open = lastClose !== null ? lastClose : prices[0]; // Usa o último fechamento como abertura, se disponível
      const close = prices[prices.length - 1];
      const high = Math.max(...prices);
      const low = Math.min(...prices);
      lastClose = close; // Atualiza o último preço de fechamento

      chartData.push({ x: time, o: open, h: high, l: low, c: close });
      newClosePriceData.push({ x: time, y: close });
      volumeChartData.push({
        x: time,
        y: volume,
        backgroundColor: close >= open ? "green" : "red",
      });
    });

    setCandleData(chartData);
    setClosePriceData(newClosePriceData);
    setVolumeData(volumeChartData);
  };

  // get max price from completed orders
  const maxPrice = Math.max(
    ...completedOrders.map((order) => parseFloat(order.price))
  );
  const minPrice = Math.min(
    ...completedOrders.map((order) => parseFloat(order.price))
  );

  const options = {
    responsive: true,
    scales: {
      x: {
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "MMM-DD HH",
          },
        },
        ticks: {
          autoSkip: false,
          stepSize: 5,
        },
      },
      "y-close": {
        type: "linear",
        display: true,
        position: "left",
        id: "y-close",
        title: {
          display: false,
          text: "Price (USDt)",
        },
        max: Math.ceil(maxPrice * 1.05 * 1000) / 1000,
        min: Math.floor(minPrice * 0.95 * 1000) / 1000,
      },
      "y-volume": {
        position: "right",
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: false,
          text: "Volume (USDt)",
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: function (tooltipItem) {
            if (tooltipItem.raw.y === undefined) {
              const { o, h, l, c } = tooltipItem.raw;
              return [`Open: ${o}`, `High: ${h}`, `Low: ${l}`, `Close: ${c}`];
            } else {
              const volume = tooltipItem.raw.y;
              return `Volume: ${formatVolume(volume)}`;
            }
          },
        },
      },
    },
  };

  const data = {
    datasets: [
      {
        label: "OLUSDT Price",
        data: candleData,
        borderColor: "rgba(255, 99, 132, 1)",
        barThickness: "flex",
        maxBarThickness: 8,
        yAxisID: "y-close",
      },
      /*{
        label: 'Close Price',
        data: closePriceData, 
        type: 'line', 
        backgroundColor: 'blue', 
        borderColor: 'rgb(54, 162, 235)', 
        borderWidth: 2, 
        yAxisID: 'y-close', 
      },*/
      {
        label: "Volume",
        data: volumeData,
        type: "bar",
        barThickness: "flex",
        maxBarThickness: 12,
        yAxisID: "y-volume",
      },
    ],
  };

  return (
    <div className="justify-center">
      {candleData.length > 0 ? (
        <>
          <h3 className="text-xl font-bold pb-4">Candlestick Chart</h3>
          <Chart type="candlestick" data={data} options={options} />
        </>
      ) : (
        <p>Loading...</p>
      )}
    </div>
  );
};

export default CandlestickChart;
